/**
 * FileContext - Manages file-related state and operations
 * 
 * Handles:
 * - Current file data (URP and Installation)
 * - Waypoints and features
 * - Nodes (groupings of waypoints)
 * - Parser instances
 * - Waypoint renumbering helper
 */
import { createContext, useContext, useState, useCallback } from 'react';
import { URPParser } from '../parser/URPParser.js';
import { InstallationParser } from '../parser/InstallationParser.js';

const FileContext = createContext(null);

export const useFile = () => {
  const context = useContext(FileContext);
  if (!context) {
    throw new Error('useFile must be used within FileProvider');
  }
  return context;
};

export const FileProvider = ({ children }) => {
  const [parser] = useState(() => new URPParser());
  const [installationParser] = useState(() => new InstallationParser());
  
  // File state
  const [currentFileData, setCurrentFileData] = useState(null);
  const [currentInstallationData, setCurrentInstallationData] = useState(null);
  const [currentWaypoints, setCurrentWaypoints] = useState([]);
  const [currentFeatures, setCurrentFeatures] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [currentFileName, setCurrentFileName] = useState('');
  const [currentInstallationFileName, setCurrentInstallationFileName] = useState('');

  // Helper function to renumber and reorder waypoints based on current node order
  const renumberWaypoints = useCallback((nodesToProcess, waypoints) => {
    // Build new waypoint array in execution order
    const reorderedWaypoints = [];
    const indexMapping = new Map();
    let waypointNumber = 1;

    nodesToProcess.forEach(node => {
      const originalTypes = node._originalTypes || node.indices.map(idx => waypoints[idx]?.processType);

      node.indices.forEach((oldIndex, positionInNode) => {
        if (waypoints[oldIndex]) {
          const newIndex = reorderedWaypoints.length;
          indexMapping.set(oldIndex, newIndex);

          const originalWaypoint = waypoints[oldIndex];
          const stableId = originalWaypoint._stableId || `wp-${Date.now()}-${oldIndex}`;
          const typeAtThisPosition = originalTypes[positionInNode];

          reorderedWaypoints.push({
            ...originalWaypoint,
            number: waypointNumber++,
            processType: typeAtThisPosition,
            _stableId: stableId
          });
        }
      });
    });

    const updatedNodes = nodesToProcess.map(node => {
      const { _originalTypes, ...cleanNode } = node;
      return {
        ...cleanNode,
        indices: cleanNode.indices.map(oldIndex => indexMapping.get(oldIndex)),
        _stableId: node._stableId,
        _parentNode: node._parentNode
      };
    });

    return { waypoints: reorderedWaypoints, nodes: updatedNodes };
  }, []);

  const value = {
    // Parsers
    parser,
    installationParser,
    
    // File state
    currentFileData,
    setCurrentFileData,
    currentInstallationData,
    setCurrentInstallationData,
    currentWaypoints,
    setCurrentWaypoints,
    currentFeatures,
    setCurrentFeatures,
    nodes,
    setNodes,
    currentFileName,
    setCurrentFileName,
    currentInstallationFileName,
    setCurrentInstallationFileName,
    
    // Reordering helper
    renumberWaypoints,
  };

  return <FileContext.Provider value={value}>{children}</FileContext.Provider>;
};
